/**
 * treatmentGuides.js
 *
 * Reference-guide content shown alongside the submission wizard so reporters
 * can identify which AE code / category an incident falls under. This is a
 * READ-ONLY aid — it does not drive the form, it just mirrors the printed
 * "AE Codes" reference sheets clinicians already use.
 *
 * Source: "LHR AE Codes 2026 V2" (clinical reference doc). Each treatment gets
 * an entry keyed by its treatmentTypes `value` (e.g. 'LHR'). Add a new key here
 * — using the same shape — to scaffold the guide for another treatment. When a
 * treatment has no guide, the wizard simply hides the right rail.
 *
 * Shape:
 *   {
 *     title:   string,           // guide heading
 *     intro:   string,           // one-line description of what AE codes are
 *     proceed: string[],         // shared "how to proceed" steps (same for all codes)
 *     codes: [{
 *       code:     'Code 1'..'Code 5',  // matches severityCodes `value`
 *       label:    string,              // the code's clinical name
 *       image:    string | null,       // /public asset — an illustrative example
 *       whatItIs: string[],            // the conditions this code covers
 *       causes:   string[],            // how it's typically caused
 *     }],
 *   }
 *
 * Images live under /public/guides/<treatment>/ and are referenced by URL so
 * they stay out of the JS bundle and load lazily.
 *
 * TODO (pre-release): like eventVocabularies.js, this should eventually be
 * config-driven from admin_config. Kept hardcoded until the vocab migration
 * lands so the two stay in lockstep.
 */

import { LHR_REPORTER_CATEGORY_LIMITS } from './eventVocabularies.js';

const LHR_GUIDE = {
  title: 'LHR AE codes',
  intro:
    'An adverse event (AE) is a negative skin response following treatment. Use the reference below to identify the code, then pick the matching severity and category on the left.',
  proceed: [
    'Remain calm & reassure the client',
    'Take photos',
    'Apply cooling & Post Laser Aid gel',
    'Report this to your manager',
    'Complete & send the incident report',
  ],
  codes: [
    {
      code: 'Code 1',
      label: 'Skin rash',
      image: '/guides/lhr/code1.png',
      whatItIs: [...LHR_REPORTER_CATEGORY_LIMITS['Code 1']],
      causes: [
        'Very dark / coarse hair',
        'Very dry skin',
        'Warm / muggy weather',
        'Client is naturally a strong responder',
        'No aftercare advice given or followed by the client',
        'No post-laser products applied or used by the client',
      ],
    },
    {
      code: 'Code 2',
      label: 'Rare complication',
      image: '/guides/lhr/code2.png',
      whatItIs: [...LHR_REPORTER_CATEGORY_LIMITS['Code 2']],
      causes: [
        'Folliculitis — heat inflames the follicle, allowing bacteria to enter',
      ],
    },
    {
      code: 'Code 3',
      label: 'Therapist error',
      image: '/guides/lhr/code3.png',
      whatItIs: [
        'Burnt make-up',
        'False tan',
        'Moles',
        'Grazing (singed hair)',
        'Grazing (pencil scratch)',
        'Heated handpiece or damaged distance gauge',
        'Lasering over clothing',
        'Lasering over long hair, brows, hair line etc.',
        'Scuffing',
        'Reflection burn on bony area',
        'Other',
      ],
      causes: [
        'Lasering over make-up or false tan',
        'Ignoring local contraindications, e.g. moles',
        'Lasering with no air',
        'Heated handpiece or damaged distance gauge',
        'Scratching the skin with white pencils',
        'Lasering over black clothing',
        'Lasering over long hair, brows, hair line etc.',
      ],
    },
    {
      code: 'Code 4',
      label: 'Tattoo reaction',
      image: '/guides/lhr/code4.png',
      whatItIs: ['Laser directly over tattoos', 'Laser over semi-permanent make-up'],
      causes: [
        'Not carrying out tattoo protocol',
        'Not visually assessing the skin',
        'Not asking safety questions',
        'Not checking the consent form',
      ],
    },
    {
      code: 'Code 5',
      label: 'Wrong wavelength',
      image: '/guides/lhr/code5.png',
      whatItIs: [
        '755 Alex on skin type 4, 5 & 6',
        '755 Alex on tanned skin',
        '1064 burn',
        'Out with parameters',
        'Eye injury',
        'Within protocol',
        'Other',
      ],
      causes: [
        'Not adhering to safety protocols',
        'Not following the safety sign-off protocol',
        'Not taking a picture of the Nd:YAG screen',
        'Not staying within treatment parameters',
      ],
    },
  ],
};

// Keyed by treatmentTypes `value`. Add further treatments here.
export const TREATMENT_GUIDES = {
  LHR: LHR_GUIDE,
};

/** Return the guide for a treatment value, or null when none exists. */
export function getTreatmentGuide(treatment) {
  if (!treatment) return null;
  return TREATMENT_GUIDES[treatment] || null;
}
